/**
 * Builds the multi-sheet Excel/Word/PDF export for the Recoverable Transactions
 * workspace (MOR-738).
 *
 * One workbook, sheets are views + claims:
 *   - "Claims Summary" — one row per statutory head (claim), with the agent
 *     outcome and the practitioner's decision roll-up.
 *   - "All Transactions" — every active transaction across the selected heads,
 *     with the agent's reasoning, defence indicators, and source documents.
 *   - "Bank Lines"     — every underlying bank-transaction line behind those
 *     records, with per-line decisions/notes and the source statement.
 *   - "Evidence"       — every evidence citation, with the referenced
 *     transaction snippet and the source document.
 *   - one sheet per head — that claim's transactions.
 *
 * Money is written as real numbers (debits negative) and dates as real Date
 * cells so the practitioner can sort / sum / filter natively in Excel. A head
 * selection (empty = all) scopes which per-claim sheets are emitted and which
 * rows feed the other sheets.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import { parseDateCell } from "@/components/jobs/phases/gather/summarization/bankTransactions/buildBankTransactionsExport";
import {
  agentText,
  fmtDate,
  fmtMoney,
  outcomeDisplay,
  titleCase,
  DECISION_BADGE,
  HEAD_ELEMENTS,
  PURPOSE_EVIDENCE_BADGE,
  RISK_BADGE,
  type FlaggedHeadKey,
  type PurposeEvidence,
  type WorkspaceUI,
} from "./flaggedShared";

/** Row shape returned by publicQueries.listFlagsForExport. */
export interface FlagExportRow {
  flagId: string;
  headKey: FlaggedHeadKey;
  headLabel: string;
  provision: string;
  transactionDate: string | null;
  isGrouped: boolean;
  counterpartyName: string;
  counterpartyRelationship: string | null;
  description: string;
  amount: number;
  quantum: number | null;
  banding: "large" | "medium" | "small";
  risk: "high" | "medium" | "low" | null;
  decision: string;
  directorName: string | null;
  isManualFlag: boolean;
  needsReview: boolean;
  valuationRequired: boolean;
  reasoning: string | null;
  recoveryNote: string | null;
  windowBasis: string | null;
  insolvencyBasis: string | null;
  purposeBasis: string | null;
  purposeEvidence: PurposeEvidence | null;
  defenceIndicators: string[];
  parallelClaims: string[];
  transactions: Array<{
    date: string | null;
    description: string;
    amount: number;
    direction: string;
    documentFileName: string | null;
    lineDecision: string | null;
    lineNote: string | null;
  }>;
  evidence: Array<{
    note: string;
    detail: string | null;
    documentFileName: string | null;
  }>;
  sourceDocuments: string[];
}

const FLAG_HEADERS = [
  "Claim",
  "Provision",
  "Date",
  "Counterparty",
  "Relationship",
  "Description",
  "Amount",
  "Recoverable",
  "Banding",
  "Risk",
  "Decision",
  "Director",
  "Added Manually",
  "Needs Review",
  "Reasoning",
  "Recovery Note",
  // Claims with a purpose / fault element the practitioner assesses; blank on
  // the claims whose elements the transaction record settles.
  "Element To Assess",
  "Material On File",
  "Circumstances Surfaced",
  "Defence Indicators",
  "Parallel Claims",
  "Source Documents",
];

function flagRows(
  flags: FlagExportRow[],
  headName: (f: FlagExportRow) => string,
): ExportSheet["rows"] {
  return flags.map((f) => {
    const subjective = HEAD_ELEMENTS[f.headKey]?.subjective;
    return [
      headName(f),
      f.provision,
      // Grouped rows aggregate many underlying transactions and have no
      // single date — mirror the table's "Various".
      f.isGrouped ? "Various" : parseDateCell(f.transactionDate ?? undefined),
      f.counterpartyName,
      f.counterpartyRelationship ? titleCase(f.counterpartyRelationship) : "",
      f.description,
      f.amount,
      f.quantum ?? "",
      titleCase(f.banding),
      f.risk ? RISK_BADGE[f.risk].label : "",
      DECISION_BADGE[f.decision]?.label ?? titleCase(f.decision),
      f.directorName ?? "",
      f.isManualFlag ? "Yes" : "No",
      f.needsReview ? "Yes" : "No",
      f.reasoning ? agentText(f.reasoning) : "",
      f.recoveryNote ? agentText(f.recoveryNote) : "",
      subjective ? `${subjective.label} (${subjective.provision})` : "",
      subjective
        ? f.purposeEvidence
          ? PURPOSE_EVIDENCE_BADGE[f.purposeEvidence].label
          : "Not assessed"
        : "",
      f.purposeBasis ? agentText(f.purposeBasis) : "",
      f.defenceIndicators.join("; "),
      f.parallelClaims.join("; "),
      f.sourceDocuments.join("; "),
    ];
  });
}

export function buildVoidableTransactionsExport(
  workspace: WorkspaceUI,
  flags: FlagExportRow[],
  selectedHeadKeys: FlaggedHeadKey[],
): ExportContent {
  const headSet =
    selectedHeadKeys.length > 0 ? new Set(selectedHeadKeys) : null;
  const heads = workspace.heads.filter(
    (h) => !headSet || headSet.has(h.headKey),
  );
  const headByKey = new Map(workspace.heads.map((h) => [h.headKey, h]));
  const headName = (f: FlagExportRow) =>
    headByKey.get(f.headKey)?.name ?? f.headLabel;

  // listFlagsForExport already sorts by recoverable (quantum, falling back to
  // amount) descending — keep that within each claim, ordered by the
  // workspace's head order across claims.
  const headIdx = new Map(workspace.heads.map((h, i) => [h.headKey, i]));
  const scoped = flags
    .filter((f) => !headSet || headSet.has(f.headKey))
    .sort(
      (a, b) => (headIdx.get(a.headKey) ?? 99) - (headIdx.get(b.headKey) ?? 99),
    );

  const summarySheet: ExportSheet = {
    name: "Claims Summary",
    headers: [
      "Claim",
      "Provision",
      "Outcome",
      "Transactions",
      "Total Value",
      "Pending",
      "Agreed",
      "Disagreed",
      "Agreed Value",
      "Recoverable Value",
      "Element To Assess",
      "Recoverable Without Documented Material",
      "Window From",
      "Window To",
      "Headline",
    ],
    rows: heads.map((h) => [
      h.name,
      h.provision,
      outcomeDisplay(h).label,
      h.flagCount,
      h.totalValue,
      h.decisions.pending,
      h.decisions.accepted,
      h.decisions.rejected,
      h.decisions.acceptedValue,
      h.decisions.recoverableValue,
      h.subjectiveElement
        ? `${h.subjectiveElement.label} (${h.subjectiveElement.provision})`
        : "",
      h.subjectiveElement ? h.subjectiveElement.undocumentedValue : "",
      parseDateCell(h.reviewWindow.start ?? undefined),
      parseDateCell(h.reviewWindow.end ?? undefined),
      h.headline ? agentText(h.headline) : "",
    ]),
  };

  const allSheet: ExportSheet = {
    name: "All Transactions",
    headers: FLAG_HEADERS,
    rows: flagRows(scoped, headName),
  };

  // Every underlying bank line, record by record. Debits negative per the
  // bank-export convention.
  const txnSheet: ExportSheet = {
    name: "Bank Lines",
    headers: [
      "Claim",
      "Counterparty",
      "Transaction Summary",
      "Date",
      "Line Description",
      "Debit",
      "Credit",
      "Line Decision",
      "Line Note",
      "Source Document",
    ],
    rows: scoped.flatMap((f) =>
      f.transactions.map((t) => [
        headName(f),
        f.counterpartyName,
        f.description,
        parseDateCell(t.date ?? undefined),
        t.description,
        t.direction === "debit" ? -t.amount : "",
        t.direction === "credit" ? t.amount : "",
        t.lineDecision ? titleCase(t.lineDecision) : "",
        t.lineNote ?? "",
        t.documentFileName ?? "",
      ]),
    ),
  };

  const evidenceSheet: ExportSheet = {
    name: "Evidence",
    headers: [
      "Claim",
      "Counterparty",
      "Evidence",
      "Transaction Detail",
      "Source Document",
    ],
    rows: scoped.flatMap((f) =>
      f.evidence.map((e) => [
        headName(f),
        f.counterpartyName,
        agentText(e.note),
        e.detail ?? "",
        e.documentFileName ?? "",
      ]),
    ),
  };

  // One sheet per claim that has flags.
  const headSheets: ExportSheet[] = heads
    .map((h) => ({
      head: h,
      flags: scoped.filter((f) => f.headKey === h.headKey),
    }))
    .filter((entry) => entry.flags.length > 0)
    .map((entry) => ({
      name: entry.head.shortLabel,
      headers: FLAG_HEADERS,
      rows: flagRows(entry.flags, headName),
    }));

  return {
    title: "Recoverable Transactions",
    sheets: [summarySheet, allSheet, txnSheet, evidenceSheet, ...headSheets],
  };
}

// ---------------------------------------------------------------------------
// Per-transaction / selected-transactions export (MOR-808)
//
// Jeff Shute asked to "just print this one preference" and save it to file.
// The workspace-level export above is a workbook of every claim; this builds a
// file-note style document for a single flagged transaction (or the rows the
// practitioner ticked) — narrative first, tables underneath.
//
// `markdown` drives Word and PDF (both prefer it over `sheets`), `sheets`
// drives Excel, so one ExportContent covers all three formats.
// ---------------------------------------------------------------------------

/** Collapse a value onto one readable line; blank/empty comes back as null. */
function inline(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).replace(/\s+/g, " ").trim();
  return text.length > 0 ? text : null;
}

/** A "Label: value" line, dropped entirely when the value is blank. */
function detailLine(label: string, value: unknown): string | null {
  const text = inline(value);
  return text ? `${label}: ${text}` : null;
}

/** "17 March 2026", or "Various" for a grouped flag with no single date. */
function flagDateLabel(f: FlagExportRow): string {
  if (f.isGrouped && !f.transactionDate) return "Various";
  return f.transactionDate ? fmtDate(f.transactionDate) : "—";
}

/** The label/value pairs that head every file note, in reading order. */
function flagFields(f: FlagExportRow): Array<[string, string]> {
  const fields: Array<[string, string]> = [
    ["Claim", `${f.headLabel} (${f.provision})`],
    ["Date", flagDateLabel(f)],
    ["Counterparty", f.counterpartyName],
  ];
  if (f.counterpartyRelationship) {
    fields.push(["Relationship", titleCase(f.counterpartyRelationship)]);
  }
  if (f.directorName) fields.push(["Director", f.directorName]);
  fields.push(
    ["Amount", fmtMoney(f.amount)],
    ["Est recoverable", fmtMoney(f.quantum ?? f.amount)],
    ["Banding", titleCase(f.banding)],
    ["Risk", f.risk ? RISK_BADGE[f.risk].label : "Unreviewed"],
    ["Decision", DECISION_BADGE[f.decision]?.label ?? titleCase(f.decision)],
  );
  if (f.isManualFlag) fields.push(["Source", "Manual flag"]);
  if (f.needsReview) fields.push(["Needs review", "Yes"]);
  if (f.valuationRequired) fields.push(["Valuation required", "Yes"]);
  if (f.windowBasis)
    fields.push(["Statutory window", agentText(f.windowBasis)]);
  if (f.insolvencyBasis) {
    fields.push(["Insolvency basis", agentText(f.insolvencyBasis)]);
  }
  return fields;
}

/** One flag rendered as a file note. `level` is the heading depth to start at. */
function flagMarkdown(f: FlagExportRow, level: 1 | 2): string[] {
  const h = (depth: number) => "#".repeat(Math.min(level + depth, 4));
  const parts: string[] = [];

  parts.push(`${h(0)} ${f.counterpartyName} — ${f.headLabel}`, "");
  for (const [label, value] of flagFields(f)) {
    parts.push(`**${label}:** ${value}`);
  }

  parts.push("", `${h(1)} Summary`, "", agentText(f.description));

  if (f.reasoning) {
    parts.push("", `${h(1)} Why this was flagged`, "", agentText(f.reasoning));
  }
  if (f.recoveryNote) {
    parts.push("", `${h(1)} Recovery`, "", agentText(f.recoveryNote));
  }
  if (f.defenceIndicators.length > 0) {
    parts.push("", `${h(1)} Possible defences`, "");
    for (const indicator of f.defenceIndicators) {
      parts.push(`- ${agentText(indicator)}`);
    }
  }
  if (f.parallelClaims.length > 0) {
    parts.push("", `${h(1)} Also captured by`, "", f.parallelClaims.join(", "));
  }
  // Stacked blocks rather than wide tables: a seven-column table does not fit
  // an A4 page in either the Word or the PDF renderer, and the overflow is
  // what the practitioner sees first.
  if (f.transactions.length > 0) {
    parts.push("", `${h(1)} Underlying transactions`, "");
    f.transactions.forEach((t, i) => {
      if (i > 0) parts.push("");
      // A heading, not a "**Label:** value" line - the PDF renderer treats
      // the latter as a label and splices a colon in after it.
      parts.push(
        `${h(2)} ${i + 1}. ${t.date ? fmtDate(t.date) : "Date unknown"} — ${fmtMoney(t.amount)} ${titleCase(t.direction)}`,
        "",
      );
      const lines = [
        inline(t.description),
        detailLine(
          "Decision",
          t.lineDecision ? titleCase(t.lineDecision) : null,
        ),
        detailLine("Note", t.lineNote),
        detailLine("Source", t.documentFileName),
      ].filter((line): line is string => line !== null);
      parts.push(...lines);
    });
  }
  if (f.evidence.length > 0) {
    parts.push("", `${h(1)} Evidence`, "");
    f.evidence.forEach((e, i) => {
      if (i > 0) parts.push("");
      parts.push(`${h(2)} Evidence ${i + 1}`, "");
      const note = inline(agentText(e.note));
      if (note) parts.push(note);
      const lines = [
        detailLine("Detail", e.detail),
        detailLine("Source", e.documentFileName),
      ].filter((line): line is string => line !== null);
      parts.push(...lines);
    });
  }
  if (f.sourceDocuments.length > 0) {
    parts.push("", `${h(1)} Source documents`, "");
    for (const doc of f.sourceDocuments) parts.push(`- ${doc}`);
  }

  return parts;
}

/**
 * File-note export for one flagged transaction, or for the rows a
 * practitioner has selected. Pass the flags in the order they should read.
 */
export function buildFlagNoteExport(flags: FlagExportRow[]): ExportContent {
  const single = flags.length === 1 ? flags[0] : null;
  const title = single
    ? `Voidable Transaction — ${single.counterpartyName}`
    : `Voidable Transactions — ${flags.length} selected`;

  const body: string[] = [`# ${title}`, ""];
  if (single) {
    // Drop the redundant per-flag heading — the document title already names
    // the counterparty and claim.
    body.push(...flagMarkdown(single, 1).slice(2));
  } else {
    const total = flags.reduce((sum, f) => sum + (f.quantum ?? f.amount), 0);
    body.push(
      `**Transactions:** ${flags.length}`,
      `**Total est recoverable:** ${fmtMoney(total)}`,
      "",
    );
    flags.forEach((f, i) => {
      if (i > 0) body.push("", "---");
      body.push("", ...flagMarkdown(f, 2));
    });
  }

  const detailSheet: ExportSheet = single
    ? {
        name: "Transaction",
        headers: ["Field", "Value"],
        rows: [
          ...flagFields(single).map(([label, value]) => [label, value]),
          ["Summary", agentText(single.description)],
          ["Reasoning", single.reasoning ? agentText(single.reasoning) : ""],
          [
            "Recovery Note",
            single.recoveryNote ? agentText(single.recoveryNote) : "",
          ],
          ["Defence Indicators", single.defenceIndicators.join("; ")],
          ["Parallel Claims", single.parallelClaims.join("; ")],
          ["Source Documents", single.sourceDocuments.join("; ")],
        ],
      }
    : {
        name: "Transactions",
        headers: FLAG_HEADERS,
        rows: flagRows(flags, (f) => f.headLabel),
      };

  const sheets: ExportSheet[] = [detailSheet];

  const lineRows = flags.flatMap((f) =>
    f.transactions.map((t) => [
      f.counterpartyName,
      parseDateCell(t.date ?? undefined),
      t.description,
      t.direction === "debit" ? -t.amount : "",
      t.direction === "credit" ? t.amount : "",
      t.lineDecision ? titleCase(t.lineDecision) : "",
      t.lineNote ?? "",
      t.documentFileName ?? "",
    ]),
  );
  if (lineRows.length > 0) {
    sheets.push({
      name: "Transaction Lines",
      headers: [
        "Counterparty",
        "Date",
        "Description",
        "Debit",
        "Credit",
        "Line Decision",
        "Line Note",
        "Source Document",
      ],
      rows: lineRows,
    });
  }

  const evidenceRows = flags.flatMap((f) =>
    f.evidence.map((e) => [
      f.counterpartyName,
      agentText(e.note),
      e.detail ?? "",
      e.documentFileName ?? "",
    ]),
  );
  if (evidenceRows.length > 0) {
    sheets.push({
      name: "Evidence",
      headers: [
        "Counterparty",
        "Evidence",
        "Transaction Detail",
        "Source Document",
      ],
      rows: evidenceRows,
    });
  }

  return { title, markdown: body.join("\n"), sheets };
}

/** Filesystem-safe base name for a per-transaction / selected export. */
export function flagNoteFileName(flags: FlagExportRow[]): string {
  if (flags.length === 1) {
    const f = flags[0];
    const slug = `${f.headLabel}_${f.counterpartyName}`
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 80);
    return slug.length > 0 ? slug : "Voidable_Transaction";
  }
  return `Voidable_Transactions_${flags.length}_selected`;
}
